/**
 * Offline Service - PWA offline support with IndexedDB storage and sync queue
 *
 * Features:
 * - Monitor online/offline status
 * - Cache API GET responses in IndexedDB
 * - Queue mutations (POST/PUT/DELETE) when offline
 * - Auto-sync queued mutations when back online
 * - Event-based notifications
 */

const DB_NAME = 'ActivityTrackerDB';
const DB_VERSION = 1;
const CACHE_STORE = 'apiCache';
const QUEUE_STORE = 'syncQueue';

class OfflineService {
  constructor() {
    this.db = null;
    this._online = navigator.onLine;
    this._listeners = new Map();
    this._initPromise = null;

    // Bind network events
    this._handleOnline = () => {
      this._online = true;
      this._notify('status', true);
      this.syncQueue(); // Auto-sync when back online
    };
    this._handleOffline = () => {
      this._online = false;
      this._notify('status', false);
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('online', this._handleOnline);
      window.addEventListener('offline', this._handleOffline);
    }
  }

  /**
   * Initialize IndexedDB
   */
  async init() {
    if (this._initPromise) return this._initPromise;
    
    this._initPromise = new Promise((resolve, reject) => {
      if (!('indexedDB' in window)) {
        console.warn('IndexedDB not available - offline features disabled');
        resolve(null);
        return;
      }

      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        // Store for API GET response cache
        if (!db.objectStoreNames.contains(CACHE_STORE)) {
          const store = db.createObjectStore(CACHE_STORE, { keyPath: 'url' });
          store.createIndex('timestamp', 'timestamp', { unique: false });
        }
        
        // Store for queued mutations
        if (!db.objectStoreNames.contains(QUEUE_STORE)) {
          const store = db.createObjectStore(QUEUE_STORE, { 
            keyPath: 'id',
            autoIncrement: true 
          });
          store.createIndex('timestamp', 'timestamp', { unique: false });
          store.createIndex('status', 'status', { unique: false });
        }
      };

      request.onsuccess = (event) => {
        this.db = event.target.result;
        resolve(this.db);
      };

      request.onerror = (event) => {
        console.error('IndexedDB error:', event.target.error);
        reject(event.target.error);
      };
    });

    return this._initPromise;
  }

  /**
   * Check if online
   */
  isOnline() {
    return this._online;
  }

  /**
   * Listen for status changes
   */
  onStatusChange(callback) {
    const id = Date.now().toString();
    this._listeners.set(id, callback);
    return () => this._listeners.delete(id);
  }

  /**
   * Notify listeners
   */
  _notify(event, data) {
    this._listeners.forEach((cb) => {
      try { cb(event, data); } catch (e) { console.error('Listener error:', e); }
    });
  }

  // ─── API Cache ──────────────────────────────────────────

  /**
   * Get cached API response
   */
  async getCached(url) {
    try {
      await this.init();
      if (!this.db) return null;
      const tx = this.db.transaction(CACHE_STORE, 'readonly');
      const store = tx.objectStore(CACHE_STORE);
      const result = await new Promise((resolve) => {
        const req = store.get(url);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => resolve(null);
      });
      return result ? result.data : null;
    } catch (e) {
      console.error('getCached error:', e);
      return null;
    }
  }

  /**
   * Cache an API response
   */
  async setCached(url, data) {
    try {
      await this.init();
      if (!this.db) return;
      const tx = this.db.transaction(CACHE_STORE, 'readwrite');
      const store = tx.objectStore(CACHE_STORE);
      store.put({ url, data, timestamp: Date.now() });
    } catch (e) {
      console.error('setCached error:', e);
    }
  }

  /**
   * Clear old cache entries (older than 7 days)
   */
  async clearOldCache(maxAge = 7 * 24 * 60 * 60 * 1000) {
    try {
      await this.init();
      if (!this.db) return;
      const cutoff = Date.now() - maxAge;
      const tx = this.db.transaction(CACHE_STORE, 'readwrite');
      const store = tx.objectStore(CACHE_STORE);
      const index = store.index('timestamp');
      const range = IDBKeyRange.upperBound(cutoff);
      index.openCursor(range).onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          store.delete(cursor.value.url);
          cursor.continue();
        }
      };
    } catch (e) {
      console.error('clearOldCache error:', e);
    }
  }

  // ─── Sync Queue ─────────────────────────────────────────

  /**
   * Queue a mutation for later sync
   */
  async queueMutation(method, url, body = null) {
    try {
      await this.init();
      if (!this.db) return null;
      const tx = this.db.transaction(QUEUE_STORE, 'readwrite');
      const store = tx.objectStore(QUEUE_STORE);
      const entry = {
        method,
        url,
        body,
        timestamp: Date.now(),
        status: 'pending',
        retries: 0,
      };
      const result = await new Promise((resolve) => {
        const req = store.add(entry);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => resolve(null);
      });
      this._notify('queued', { method, url, id: result });
      return result;
    } catch (e) {
      console.error('queueMutation error:', e);
      return null;
    }
  }

  /**
   * Get all queued mutations
   */
  async getQueue() {
    try {
      await this.init();
      if (!this.db) return [];
      const tx = this.db.transaction(QUEUE_STORE, 'readonly');
      const store = tx.objectStore(QUEUE_STORE);
      const index = store.index('status');
      const range = IDBKeyRange.only('pending');
      return await new Promise((resolve) => {
        const results = [];
        const req = index.openCursor(range);
        req.onsuccess = (event) => {
          const cursor = event.target.result;
          if (cursor) {
            results.push(cursor.value);
            cursor.continue();
          } else {
            resolve(results);
          }
        };
        req.onerror = () => resolve([]);
      });
    } catch (e) {
      console.error('getQueue error:', e);
      return [];
    }
  }

  /**
   * Remove a mutation from queue after successful sync
   */
  async removeFromQueue(id) {
    try {
      await this.init();
      if (!this.db) return;
      const tx = this.db.transaction(QUEUE_STORE, 'readwrite');
      const store = tx.objectStore(QUEUE_STORE);
      store.delete(id);
    } catch (e) {
      console.error('removeFromQueue error:', e);
    }
  }

  /**
   * Mark mutation as failed
   */
  async markFailed(id) {
    try {
      await this.init();
      if (!this.db) return;
      const tx = this.db.transaction(QUEUE_STORE, 'readwrite');
      const store = tx.objectStore(QUEUE_STORE);
      const req = store.get(id);
      req.onsuccess = () => {
        const entry = req.result;
        if (entry) {
          entry.retries = (entry.retries || 0) + 1;
          if (entry.retries >= 5) {
            entry.status = 'failed';
          } else {
            entry.status = 'pending';
          }
          store.put(entry);
        }
      };
    } catch (e) {
      console.error('markFailed error:', e);
    }
  }

  /**
   * Sync all queued mutations
   */
  async syncQueue() {
    if (!this._online) return { synced: 0, failed: 0 };
    
    const queue = await this.getQueue();
    if (queue.length === 0) return { synced: 0, failed: 0 };

    let synced = 0;
    let failed = 0;

    for (const entry of queue) {
      try {
        const token = localStorage.getItem('auth_token');
        const headers = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const response = await fetch(entry.url, {
          method: entry.method,
          headers,
          body: entry.body ? JSON.stringify(entry.body) : undefined,
        });

        if (response.ok) {
          await this.removeFromQueue(entry.id);
          synced++;
        } else {
          await this.markFailed(entry.id);
          failed++;
        }
      } catch (e) {
        console.error('Sync error for', entry.url, e);
        await this.markFailed(entry.id);
        failed++;
      }
    }

    if (synced > 0) {
      this._notify('synced', { synced, failed });
    }

    return { synced, failed };
  }

  /**
   * Get queue count
   */
  async getQueueCount() {
    const queue = await this.getQueue();
    return queue.length;
  }

  /**
   * Get total cached items count
   */
  async getCacheCount() {
    try {
      await this.init();
      if (!this.db) return 0;
      const tx = this.db.transaction(CACHE_STORE, 'readonly');
      const store = tx.objectStore(CACHE_STORE);
      return await new Promise((resolve) => {
        const req = store.count();
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => resolve(0);
      });
    } catch (e) {
      return 0;
    }
  }

  /**
   * Cleanup: remove event listeners
   */
  destroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this._handleOnline);
      window.removeEventListener('offline', this._handleOffline);
    }
    this._listeners.clear();
  }
}

export const offlineService = new OfflineService();
export default offlineService;
